import { useQuery } from '@apollo/client/react'
import { SplitFactoryProvider } from '@splitsoftware/splitio-react'
import * as React from 'react'
import { useEffect, useMemo, useState } from 'react'

import getFeatureFlipAttributesFromAccount from './getAttributesFromAccount'
import getEnvironmentFromURL from './getEnvironmentFromURL'
import { ORG_DATA_QUERY } from './queries/currentOrganization'
import { LOCATION_DATA_QUERY } from './queries/location'
import trackImpression, { setImpressionTrackingReady } from './trackImpressions'
import type { FeatureFlipAttributes, OrgChangeEvent } from './types'

export const SPLITIO_PROD_CLIENT_KEY = 'p80t4a0uumurkoiu03n901vds197chq8bssp'
export const SPLITIO_LOCAL_CLIENT_KEY = 'cv7k6ubsjas041tvs0869rd69l5i26svbg8r'
export const SPLITIO_STAGING_CLIENT_KEY = '7ep659kbp3peet2964pukms48g4ubsn83b9s'

/** @todo Ideally pull this from the window.appshell global. Couldn't do it here due to race condition. */
const ORG_CHANGE_EVENT = 'appshell__organization_event'

export const FeatureFlipAttributesContext =
  React.createContext<FeatureFlipAttributes | null>(null)

export const IsImpersonationContext = React.createContext<boolean>(false)

/** True when no Split client will be created: the org query settled with an
 *  error or without an organization, or there is no FeaturesWrapper. */
const SplitUnavailableContext = React.createContext<boolean>(true)
export function useIsSplitUnavailable(): boolean {
  return React.useContext(SplitUnavailableContext)
}

export function FeaturesWrapper({
  mockSplits,
  children,
  environment: environmentProp,
}: {
  mockSplits?: SplitIO.MockedFeaturesMap
  children: React.ReactNode
  environment?: string
}) {
  // Use no-cache to see if it helps avoid conflicts with other Apollo clients
  const { data, loading } = useQuery(ORG_DATA_QUERY, {
    fetchPolicy: 'no-cache',
  })
  const { data: locationData } = useQuery(LOCATION_DATA_QUERY)

  const queryOrgId = data?.account?.currentOrganization?.id
  const [switchedOrgId, setSwitchedOrgId] = useState<string | null>(null)
  const currentOrgId = switchedOrgId || queryOrgId

  const [featureFlipAttributes, setFeatureFlipAttributes] =
    useState<FeatureFlipAttributes | null>(null)

  useEffect(() => {
    const attributes = getFeatureFlipAttributesFromAccount(
      data?.account,
      locationData,
    )
    if (attributes) {
      setFeatureFlipAttributes(attributes)
      setImpressionTrackingReady(true)
    } else {
      setImpressionTrackingReady(false)
    }
  }, [currentOrgId, data, locationData])

  useEffect(() => {
    function handleOrgSwitch(event: OrgChangeEvent) {
      setSwitchedOrgId(event.detail.organizationId)
    }
    window.addEventListener(ORG_CHANGE_EVENT, handleOrgSwitch as EventListener)
    return function cleanup() {
      window.removeEventListener(
        ORG_CHANGE_EVENT,
        handleOrgSwitch as EventListener,
      )
    }
  }, [])

  // Undefined until the org id resolves. SplitFactoryProvider tolerates a
  // missing config and publishes a null-factory context, so it stays mounted
  // from first paint and children never change tree position.
  // Identity matters: the SDK factory is cached by this object's reference, so a
  // new object per render would destroy and re-initialise the factory each time.
  const splitConfig = useMemo<SplitIO.IBrowserSettings | undefined>(() => {
    if (!currentOrgId) {
      return undefined
    }

    const config: SplitIO.IBrowserSettings = {
      core: {
        authorizationKey: SPLITIO_PROD_CLIENT_KEY,
        key: currentOrgId,
        trafficType: 'organization',
      },
      // Track experiments to Segment
      impressionListener: {
        logImpression: trackImpression,
      },
    }

    // Support mock / localhost mode
    // https://help.split.io/hc/en-us/articles/360020448791-JavaScript-SDK#localhost-mode
    if (mockSplits) {
      config.core.authorizationKey = 'localhost'
      config.features = mockSplits
    } else {
      // Support environments (either forced by prop, or detected)
      const environment = environmentProp ?? getEnvironmentFromURL()

      if (environment === 'staging') {
        config.core.authorizationKey = SPLITIO_STAGING_CLIENT_KEY
      }
      if (environment === 'local') {
        config.core.authorizationKey = SPLITIO_LOCAL_CLIENT_KEY
      }
      if (environment === 'production') {
        config.core.authorizationKey = SPLITIO_PROD_CLIENT_KEY
      }
    }

    return config
  }, [currentOrgId, environmentProp, mockSplits])

  return (
    // `updateOnSdkTimedout` publishes the client when the SDK gives up on its
    // first load (`startup.readyTimeout`, 10s by default) instead of leaving
    // it `null` until a later SDK_READY. A null client then only means "not
    // settled yet", so callers that must not act on the default treatment can
    // wait for it. `useSplitEnabled` keeps timed-out evaluations on the
    // cached-or-default treatment, as before.
    <SplitFactoryProvider config={splitConfig} updateOnSdkTimedout>
      <IsImpersonationContext.Provider
        value={data?.account?.isImpersonation ?? false}
      >
        <FeatureFlipAttributesContext.Provider value={featureFlipAttributes}>
          <SplitUnavailableContext.Provider value={!loading && !currentOrgId}>
            {children}
          </SplitUnavailableContext.Provider>
        </FeatureFlipAttributesContext.Provider>
      </IsImpersonationContext.Provider>
    </SplitFactoryProvider>
  )
}
